import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Check } from "lucide-react";

/**
 * Reusable MultiSelect component.
 * Allows multiple item selection with checkbox indicators and chip count display.
 */
const MultiSelect = ({
  value = [],
  options = [],
  onChange,
  label,
  placeholder = "Select options...",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange?.(newValue);
  };

  const selectedLabels = options
    .filter((opt) => value.includes(opt.value))
    .map((opt) => opt.label);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-2 ml-1">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-5 py-3 bg-zinc-50 border border-transparent rounded-xl transition-all duration-300 font-bold text-xs ${
          isOpen
            ? "bg-white border-zinc-900 ring-4 ring-zinc-900/5 shadow-2xs"
            : "hover:bg-zinc-100"
        }`}
      >
        <span className="text-zinc-900 truncate">
          {selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder}
        </span>
        <div className="flex items-center gap-2">
          {selectedLabels.length > 0 && (
            <span className="bg-zinc-900 text-white text-[10px] px-2 py-0.5 rounded-full">
              {selectedLabels.length}
            </span>
          )}
          <ChevronDown
            size={16}
            className={`text-zinc-400 transition-transform duration-300 ${
              isOpen ? "rotate-180 text-zinc-900" : ""
            }`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white border border-zinc-200 rounded-xl shadow-2xl z-[100] overflow-hidden animate-slideIn">
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.length === 0 ? (
              <div className="px-5 py-3 text-xs text-zinc-400 font-medium">No options available</div>
            ) : (
              options.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => toggleOption(opt.value)}
                  className={`px-5 py-3 text-xs font-bold transition-colors cursor-pointer flex items-center justify-between ${
                    value.includes(opt.value)
                      ? "bg-zinc-50 text-zinc-900"
                      : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                        value.includes(opt.value)
                          ? "bg-zinc-900 border-zinc-900"
                          : "bg-white border-zinc-200"
                      }`}
                    >
                      {value.includes(opt.value) && <Check size={12} className="text-white" />}
                    </div>
                    {opt.label}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
